import { Card, Flex, Text, type CardProps } from '@mantine/core'
import { clsx } from 'clsx'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'

import { getProductAvailabilityById } from '@flecto/api/store'
import { AvatarProduct } from '@flecto/ui'

import { useAccessibilityProps } from '../../hooks/useAccessibilityProps'
import { useProductSlugEncode } from '../../hooks/useProductSlugEncode'
import { useStoreDates } from '../../hooks/useStoreDates'
import { useStoreDetails } from '../../hooks/useStoreDetails'
import { useStoreProductAvailabilityMerge } from '../../hooks/useStoreProductAvailabilityMerge'
import { useStorePathBuilder } from '../../hooks/useStorePathBuilder'
import { type ProductWithAvailability } from '../../types/ProductWithAvailability'
import { ButtonProductAddToCart } from '../ButtonProductAddToCart/ButtonProductAddToCart'
import { SelectProductQuantity } from '../SelectProductQuantity/SelectProductQuantity'
import { TextCurrencyStore } from '../TextCurrencyStore/TextCurrencyStore'
import { TextProductAvailability } from '../TextProductAvailability/TextProductAvailability'
import styles from './styles.module.css'

export type CardProductProps = Omit<CardProps, 'children'> & {
  product?: ProductWithAvailability
  isLoadingAvailability?: boolean
}

export function CardProduct({
  product,
  isLoadingAvailability,
  ...props
}: CardProductProps) {
  const [isLoadingAvailabilityLocal, setIsLoadingAvailabilityLocal] =
    useState(false)

  const [localProduct, setLocalProduct] = useState<
    ProductWithAvailability | undefined
  >(product)
  const [quantityToAdd, setQuantityToAdd] = useState(1)

  const acessibilityProps = useAccessibilityProps({
    type: 'product',
    content: product?.title ?? '',
  })
  useEffect(() => {
    setLocalProduct(product)
  }, [product])

  const { startAt, endAt } = useStoreDates()

  const slug = useProductSlugEncode(product)

  const store = useStoreDetails()

  const path = useStorePathBuilder()

  const classes = clsx(styles.card, props.className)

  const link = path(`/products/${slug}`)

  const handleGetProductAvailability = useCallback(
    async (quantityToAdd: number) => {
      setIsLoadingAvailabilityLocal(true)
      try {
        const pa = await getProductAvailabilityById(
          store?.domain ?? '',
          product?.id ?? '',
          {
            start_at: startAt ?? '',
            end_at: endAt ?? '',
            quantity: quantityToAdd,
          }
        )

        const newLocalProduct = useStoreProductAvailabilityMerge({
          productAvailability: pa,
          product,
          quantityToAdd,
          startAt,
          endAt,
        })

        setLocalProduct(newLocalProduct)
      } finally {
        setIsLoadingAvailabilityLocal(false)
      }
    },
    [startAt, endAt, product, store?.domain]
  )

  if (!product) {
    return
  }

  return (
    <Card {...props} className={classes}>
      <Link href={link}>
        <AvatarProduct
          product={product}
          w='100%'
          h='auto'
          radius='xs'
          style={{ aspectRatio: '1/1' }}
          {...acessibilityProps}
        />
      </Link>
      <Text lineClamp={2} fw={500} h={40} component={Link} href={link}>
        {product.title}
      </Text>
      <TextCurrencyStore
        size='xl'
        fw={500}
        cents={
          store?.show_prices_with_tax
            ? (product?.cheapest_booking_cost_with_vat_cents ?? 0)
            : (product?.cheapest_booking_cost_cents ?? 0)
        }
        labelFallback=' '
      />
      <Flex mt='md' align='center' gap='xs' justify='space-between'>
        <SelectProductQuantity
          size='sm'
          value={quantityToAdd}
          withShortLabel
          onChange={(q) => {
            setQuantityToAdd(q)
            handleGetProductAvailability(q)
          }}
        />
        <TextProductAvailability
          c='dimmed'
          size='xs'
          product={localProduct}
          quantity={quantityToAdd}
          isLoading={isLoadingAvailability || isLoadingAvailabilityLocal}
        />
      </Flex>
      <ButtonProductAddToCart
        fullWidth
        size='sm'
        mt='xs'
        product={product}
        quantity={quantityToAdd}
      />
    </Card>
  )
}
